import { rem } from 'polished';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { css, styled } from 'styles';
import { tracking } from 'App/Tracking';
import PrimaryButton from 'Shared/PrimaryButton';
import { ExternalLinkIcon } from 'Shared/Typography/ExternalLink';
import { AlertBox } from '../../../Shared/Form';
import { small } from '../../../utils';
import {
  SLACK_INSTALLATION_ERROR_CODE,
  SLACK_USER_NOT_FOUND,
} from '../constants';
import { Slack as SlackIntegrationCard } from './Slack';
import { IntegrationCard } from './IntegrationCard';
import { MSTeamsLogo } from './styles';

export const Container = styled.div`
  display: grid;
  row-gap: ${rem(24)};
`;

export const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${rem(24)};
  justify-items: start;

  ${small(css`
    grid-template-columns: repeat(3, 1fr);
  `)}
`;

const ButtonContentContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.button.primary.default.textColor};
`;

interface ExternalLinkButtonProps {
  label: string;
}

const ExternalLinkButton = ({ label }: ExternalLinkButtonProps) => (
  <ButtonContentContainer>
    {label}
    <ExternalLinkIcon />
  </ButtonContentContainer>
);

enum IntegrationType {
  MSTeams = 'ms_teams',
  Slack = 'slack',
}

export const IntegrationsTab = () => {
  const { t: translate } = useTranslation('account');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const error = searchParams.get('error');

  const sendTracking = (integrationType: IntegrationType) => {
    void tracking.track('integrations-card-clicked', {
      integrationType,
    });
  };

  const errorMessage = (() => {
    switch (error) {
      case SLACK_INSTALLATION_ERROR_CODE:
        return translate('integrations.slack.connection_denied_error');
      case SLACK_USER_NOT_FOUND:
        return translate('integrations.slack.user_not_found_error');
      default:
        return null;
    }
  })();

  return (
    <Container>
      {errorMessage ? (
        <AlertBox alertType="failed" message={errorMessage} />
      ) : null}
      <GridWrapper>
        <SlackIntegrationCard
          onCardClick={() => sendTracking(IntegrationType.Slack)}
        />
        <IntegrationCard
          logo={<MSTeamsLogo />}
          name={translate('integrations.ms_teams.name')}
          description={translate('integrations.ms_teams.description')}
          type={translate('integrations.ms_teams.type')}
          button={
            <PrimaryButton
              label={
                <ExternalLinkButton
                  label={translate('integrations.ms_teams.connection_button')}
                />
              }
              onClick={() => {
                sendTracking(IntegrationType.MSTeams);
                window.open(
                  'https://teams.microsoft.com/l/app/c7d6307e-8186-438f-a33f-66b22f269324?source=app-details-dialog',
                  '_blank',
                );
              }}
            />
          }
        />
      </GridWrapper>
    </Container>
  );
};
